import React, { useCallback, useEffect, useState } from "react";

import { useTranslator } from "../../context/translator";
import Loading from "components/loading/Loading";
import Can from "components/can/Can";
import NoResults from "components/noResults/noResults";

import { getSensitiveCustomerInformationService } from "services/sensitiveCustomerInformation/services";
import Pagination from "components/pagination/Pagination";
import { getCustomerByIdService } from "services/customers/services";
import ClientCardInformationModal from "components/ClientCardInformationModal/ClientCardInformationModal";
import { DOCUMENT_TYPES, PERMISSION_CODE } from "utils/enum/enums";

const ClientCardInformation = ({ clientId }) => {
  const [cards, setCards] = useState([]);
  const { translate } = useTranslator();

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  // total number of records
  const [pageNumber, setPageNumber] = useState(0);
  // track API response time
  const [
    totalSensitiveCustomerInformation,
    setTotalSensitiveCustomerInformation,
  ] = useState(0);
  const [search, setSearch] = useState("");

  const [customer, setCustomer] = useState(null);
  const [documentSelected, setDocumentSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const viewDocument = (documentId, index) => {
    let card = cards.find((c) => c._id === documentId) || null;

    if (!card) {
      card = {
        firstName: customer?.firstName || "",
        lastName: customer?.lastName || "",
        documentName: "",
        documentDetails: "",
        last4: "",
        files: [],
        isThirdParty: false,
        index: index || 0,
        status: true,
        documentType: DOCUMENT_TYPES.CD,
        relatedTo: null,
        provider: "",
        accountNumber: "",
        issueDate: "",
        address: "",
        country: customer?.country || { id: 0, name: "No Selected" },
        state: customer?.state || { id: 0, name: "No Selected" },
        city: customer?.city || { id: 0, name: "No Selected" },
        streetAddress: customer?.streetAddress || "",
        streetAddress2: customer?.streetAddress2 || "",
        zipCode: customer?.zipCode || "",
        dateOfBirth: "",
        expirationDate: "",
        thirdParty: {
          firstName: "",
          lastName: "",
          authorized: false,
          idFiles: [],
        },
      };
    }
    setDocumentSelected(card);
    toggleModal();
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const getSensitiveDocuments = React.useCallback(async () => {
    setIsLoading(true);

    const data = await getSensitiveCustomerInformationService({
      customerId: clientId,
      page,
      search,
      pageSize: 5,
    });
    setIsLoading(false);

    setCards(data?.sensitiveCustomerInformation ?? []);
    const count = data?.totalSensitiveCustomerInformation ?? 0;
    setTotalSensitiveCustomerInformation(count);
    setPageNumber(Math.ceil(count / 5));
  }, [clientId, page, search]);

  useEffect(() => {
    getSensitiveDocuments();
  }, [getSensitiveDocuments]);

  const handleOnChangeSearch = (event) => {
    const value = event.target.value;
    setSearch(value);

    setPage(1);
  };

  const getClient = useCallback(async () => {
    if (clientId && clientId !== "new") {
      const data = await getCustomerByIdService({ customerId: clientId });
      if (data?.customer) setCustomer(data.customer);
    }
  }, [clientId]);

  useEffect(() => {
    getClient();
  }, [getClient]);

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="grid gap-6 mb-6 md:grid-cols-2  mt-2 mr-2 ml-2">
        <div>
          <label
            htmlFor="search"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("search")}
          </label>
          <input
            type="text"
            id="search"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("search") + "..."}
            onChange={handleOnChangeSearch}
          />
        </div>
        <Can permissions={[PERMISSION_CODE.CREATE_SENSITIVE_CUSTOMER_INFO]}>
          <div className="flex justify-end items-end">
            <button
              type="button"
              className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 dark:focus:ring-green-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
              onClick={viewDocument}
            >
              {translate("new-document")}
            </button>
          </div>
        </Can>
      </div>

      {!isLoading && cards.length === 0 && (
        <NoResults message={translate("no-documents-found")} />
      )}
      {cards.length === 0 && isLoading && <Loading />}
      {cards.length > 0 && (
        <>
          <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  {translate("name")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {translate("document-type")}
                </th>

                <th scope="col" className="px-6 py-3">
                  {translate("status")}
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">{translate("edit")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {cards?.map((card, index) => {
                return (
                  <tr
                    key={card._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {card.documentType === DOCUMENT_TYPES.DC
                        ? `${card.documentName}`
                        : `${card.firstName} ${card.lastName}`}
                    </th>
                    <td className="px-6 py-4">{card.documentType}</td>
                    <td className="px-6 py-4">
                      {card.status
                        ? translate("active")
                        : translate("inactive")}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                        onClick={() => {
                          viewDocument(card._id, index);
                        }}
                      >
                        {translate("view")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isLoading && <Loading />}
          {!isLoading && (
            <Pagination
              key={"paginationSensitiveCustomerInformation"}
              data={cards}
              page={page}
              pageNumber={pageNumber}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalSensitiveCustomerInformation}
            />
          )}
        </>
      )}
      <ClientCardInformationModal
        key={"createModal"}
        clientCardInformation={documentSelected}
        getInformation={getSensitiveDocuments}
        setClientCardInformation={setDocumentSelected}
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        customer={customer}
      />
    </div>
  );
};

export default ClientCardInformation;
